import React, { useState, useEffect } from 'react';
import { t } from '../tokens';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { UserProfile } from '../types';

const IS_DEVELOPMENT = import.meta.env.DEV;

interface AccountPanelProps {
  isOpen: boolean;
  onClose: () => void;
  userProfile?: UserProfile | null;
  onRequestDrivePermissions?: () => Promise<void>;
  onDisconnectDrive?: () => void;
  onManageBilling: () => void;
  onUpgrade: () => void;
  onToggleDevMode: () => void;
}

const AccountPanel: React.FC<AccountPanelProps> = ({
  isOpen,
  onClose,
  userProfile,
  onRequestDrivePermissions,
  onDisconnectDrive,
  onManageBilling,
  onUpgrade,
  onToggleDevMode,
}) => {
  const { user, signIn, signOut, isSigningIn } = useAuth();
  const { subscription, hasFeature } = useSubscription();
  const [isCloudSyncEnabled, setIsCloudSyncEnabled] = useState(false);
  const [showCloudSyncInfo, setShowCloudSyncInfo] = useState(false);

  useEffect(() => {
    setIsCloudSyncEnabled(!!userProfile);
  }, [userProfile]);

  const handleSignIn = async () => {
    try {
      await signIn();
    } catch (error) {
      console.error('❌ Sign-in error:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsCloudSyncEnabled(false);
    } catch (error) {
      console.error('❌ Sign-out error:', error);
    }
  };

  const handleCloudSyncToggle = async () => {
    if (!hasFeature('cloudSync')) {
      return;
    }

    if (userProfile && isCloudSyncEnabled) {
      const drive = await import('../utils/drive');
      drive.signOut();
      if (onDisconnectDrive) {
        onDisconnectDrive();
      }
      setIsCloudSyncEnabled(false);
      return;
    }

    if (!userProfile && onRequestDrivePermissions) {
      try {
        await onRequestDrivePermissions();
      } catch (error) {
        console.error('Failed to get Drive permissions:', error);
      }
    }
  };

  const isDevModeEnabled = subscription.isPremium && (subscription.customerId === 'dev_customer');

  return (
    <div
      className={`fixed top-0 right-0 h-full shadow-xl transition-transform duration-300 ease-in-out z-[260] ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}
      style={{ width: 'min(400px, calc(100vw - 12px))', maxWidth: 'calc(100vw - 12px)', backgroundColor: t.panelBg }}
      data-debug-id="Level-1:AccountPanel"
    >
      {/* Gold accent stripe */}
      <div className="h-0.5 flex-shrink-0" style={{ background: `linear-gradient(to right, ${t.gold}, #C4903A)` }} />
      <div className="flex flex-col h-full">
        {/* Panel Header */}
        <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid rgba(86,70,60,0.5)' }}>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Kumbh Sans, sans-serif', color: t.gold }}>Account &amp; Subscription</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-md transition-colors"
            style={{ color: t.beige }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(86,70,60,0.5)')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Panel Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">

          {/* Account Section */}
          <div>
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">ACCOUNT</h3>
            <div className="bg-gray-700 rounded-lg p-4 space-y-4">

              {!user ? (
                /* Not Signed In */
                <div className="space-y-3">
                  <div>
                    <h4 className="text-white font-medium">Sign In</h4>
                    <p className="text-sm text-gray-400">Connect with your Google account to access premium features</p>
                  </div>
                  <button
                    onClick={handleSignIn}
                    disabled={isSigningIn}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white text-gray-900 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50"
                  >
                    {isSigningIn ? (
                      <>
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-gray-900 rounded-full animate-spin"></div>
                        <span>Signing in...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        <span>Sign in with Google</span>
                      </>
                    )}
                  </button>
                </div>
              ) : (
                /* Signed In */
                <div className="space-y-4">
                  {/* User Info Display */}
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <img
                        src={user.photoURL || '/default-avatar.svg'}
                        alt={user.displayName || 'User'}
                        className="w-10 h-10 rounded-full"
                      />
                      {/* Green online dot */}
                      <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 border-2 border-gray-700 rounded-full"></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium truncate">
                        {user.displayName || 'Google User'}
                      </div>
                      <div className="text-sm text-gray-400 truncate">
                        {user.email}
                      </div>
                    </div>
                  </div>

                  {/* Cloud Sync Section */}
                  <div className="border-t border-gray-600 pt-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-white font-medium">Enable Cloud Sync</h4>
                          <button
                            onClick={() => setShowCloudSyncInfo(!showCloudSyncInfo)}
                            className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 hover:from-blue-400 hover:to-cyan-300 text-white flex items-center justify-center text-sm font-bold transition-all shadow-lg hover:shadow-cyan-500/50 hover:scale-110"
                            title="Learn more"
                          >
                            ?
                          </button>
                        </div>

                        {/* Info Popup */}
                        {showCloudSyncInfo && (
                          <div className="mt-2 p-3 bg-gray-800 border border-gray-600 rounded-lg text-sm text-gray-300">
                            <p>
                              Access your charts from any device - phone, tablet, or computer.
                              Your data syncs automatically to your Google Drive and stays up to date everywhere.
                            </p>
                          </div>
                        )}

                        {userProfile && (
                          <p className="text-xs text-green-400 mt-1">
                            ✅ Connected to Drive: {userProfile.email}
                          </p>
                        )}
                        {user && hasFeature('cloudSync') && !userProfile && (
                          <p className="text-xs text-yellow-400 mt-1">
                            ⚠️ Drive permissions needed - toggle to connect
                          </p>
                        )}
                        {!hasFeature('cloudSync') && (
                          <div className="flex items-center gap-1 mt-1">
                            <span className="text-xs bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-2 py-0.5 rounded-full font-bold">
                              ✨ Premium
                            </span>
                          </div>
                        )}
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-3">
                        <input
                          type="checkbox"
                          checked={isCloudSyncEnabled && !!userProfile}
                          onChange={handleCloudSyncToggle}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>

                  {/* Sign Out Button */}
                  <button
                    onClick={handleSignOut}
                    className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500 transition-colors"
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Subscription Section */}
          <div>
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">SUBSCRIPTION</h3>
            <div className="bg-gray-700 rounded-lg p-4 space-y-3">

              {/* Current Plan */}
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-white font-medium capitalize">{subscription.plan} Plan</span>
                  <p className="text-sm text-gray-400">
                    {subscription.isPremium
                      ? 'Access to all premium features'
                      : 'Limited to free features'
                    }
                  </p>
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-bold ${
                  subscription.isPremium
                    ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-black'
                    : 'bg-gray-600 text-white'
                }`}>
                  {subscription.isPremium ? '✨' : '🔒'}
                </div>
              </div>

              {/* Manage Subscription Button */}
              <button
                onClick={subscription.isPremium ? onManageBilling : onUpgrade}
                className={`w-full py-2 px-4 text-white font-medium rounded-lg transition-all ${
                  subscription.isPremium
                    ? 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400'
                    : 'bg-gray-600 hover:bg-gray-500'
                }`}
              >
                {subscription.isPremium ? '⚙️ Manage Billing' : '✨ Upgrade to Premium'}
              </button>

              {/* Development Mode Toggle - Only in development */}
              {IS_DEVELOPMENT && (
                <div className="pt-2 border-t border-gray-600">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-white font-medium text-sm">Development Mode</label>
                      <p className="text-xs text-gray-400">Test premium features locally</p>
                    </div>
                    <button
                      onClick={onToggleDevMode}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                        isDevModeEnabled
                          ? 'bg-green-600 hover:bg-green-700 text-white'
                          : 'bg-gray-600 hover:bg-gray-500 text-white'
                      }`}
                    >
                      {isDevModeEnabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AccountPanel;
